import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import ptkcs from '../../../Assets/Home/KCS TECH_logo.png';
import PremiumNavMenu, { navigationMenus, menuContainsPath } from './PremiumNavMenu';

import './Navbar.css';

const focusClass = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4';

function ContactButton({ onClick, mobile = false }) {
  return <Link to="/contact" onClick={onClick}
    className={`kcs-nav-contact ${focusClass} ${mobile ? 'w-full' : ''}`}>Contact us</Link>;
}

export default function Navbar({ linkClicked }) {
  const [active, setActive] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileActive, setMobileActive] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);
  const mobileRef = useRef(null);
  const toggleRef = useRef(null);
  const triggerRefs = useRef({});
  const closeTimer = useRef(null);
  const { pathname } = useLocation();
  const currentMenu = navigationMenus.find(menu => menu.id === active);

  const cancelClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const closeAll = () => {
    cancelClose();
    setActive('');
    setMobileOpen(false);
    setMobileActive('');
  };
  const navigate = () => {
    closeAll();
    linkClicked?.();
  };
  const openMenu = id => {
    cancelClose();
    setActive(id);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setActive(''), 180);
  };

  useEffect(() => {
    window.clearTimeout(closeTimer.current);
    setActive('');
    setMobileOpen(false);
    setMobileActive('');
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(closeTimer.current);
    };
  }, []);

  useEffect(() => {
    const onOutside = event => {
      if (!navRef.current?.contains(event.target) && !mobileRef.current?.contains(event.target)) {
        window.clearTimeout(closeTimer.current);
        setActive('');
        setMobileOpen(false);
      }
    };
    const onKey = event => {
      if (event.key === 'Escape') {
        window.clearTimeout(closeTimer.current);
        if (mobileOpen) toggleRef.current?.focus();
        else if (active && navRef.current?.contains(document.activeElement)) triggerRefs.current[active]?.focus();
        setActive('');
        setMobileOpen(false);
        setMobileActive('');
      }
      // Keep keyboard focus within the open mobile navigation and its toggle.
      if (event.key === 'Tab' && mobileOpen) {
        const links = [...(mobileRef.current?.querySelectorAll('a, button, summary') || [])].filter(element => element.getClientRects().length);
        const first = toggleRef.current;
        const last = links[links.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [active, mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [mobileOpen]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1280px)');
    const onResize = () => {
      window.clearTimeout(closeTimer.current);
      setActive('');
      setMobileOpen(false);
      setMobileActive('');
    };
    desktop.addEventListener('change', onResize);
    return () => desktop.removeEventListener('change', onResize);
  }, []);

  return <>
    <nav ref={navRef} aria-label="Main navigation" className="sticky top-0 z-50 border-b px-5 transition-shadow duration-300 sm:px-6 lg:px-10"
      style={{ backgroundColor: '#FFFFFF', borderColor: '#E7EDF5', boxShadow: scrolled ? '0 6px 28px rgba(15,42,78,0.07)' : 'none' }}
      onMouseLeave={scheduleClose} onMouseEnter={cancelClose}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) scheduleClose(); }}>
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-6 xl:grid xl:grid-cols-[1fr_auto_1fr] xl:h-[76px]">
        <Link to="/" onClick={navigate} aria-label="KCS Tech home" className={`shrink-0 justify-self-start rounded-lg transition-opacity hover:opacity-80 ${focusClass}`} style={{ outlineColor: '#2563EB' }}>
          <img src={ptkcs} alt="KCS Tech" className="h-10 w-auto object-contain xl:h-12" />
        </Link>
        <div className="hidden h-full items-center justify-center gap-6 xl:flex">
          <Link to="/" onClick={navigate} onMouseEnter={() => openMenu('')} aria-current={pathname === '/' ? 'page' : undefined}
            className={`kcs-nav-link py-1 text-[16px] font-normal ${focusClass}`}
            style={{ color: '#000000', borderBottomColor: pathname === '/' ? '#005EB8' : 'transparent', outlineColor: '#2563EB' }}>Home</Link>
          {navigationMenus.map(menu => {
            const expanded = active === menu.id;
            const selected = menuContainsPath(menu, pathname);
            return <button key={menu.id} ref={element => { triggerRefs.current[menu.id] = element; }} type="button"
              className={`kcs-nav-link inline-flex items-center py-1 transition duration-200 ${focusClass}`}
              style={{ color: '#000000', borderBottomColor: expanded || selected ? '#005EB8' : 'transparent', outlineColor: '#2563EB' }}
              aria-expanded={expanded} aria-controls={expanded ? `desktop-${menu.id}-menu` : undefined}
              onMouseEnter={() => openMenu(menu.id)} onClick={() => { cancelClose(); setActive(expanded ? '' : menu.id); }}
              onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); openMenu(menu.id); window.requestAnimationFrame(() => document.getElementById(`desktop-${menu.id}-menu`)?.querySelector('a')?.focus()); } }}>
              <span className="text-[16px] font-normal leading-5">{menu.label}</span>
            </button>;
          })}
        </div>
        <div className="hidden justify-self-end xl:block"><ContactButton onClick={navigate} /></div>
        <button ref={toggleRef} type="button" aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={mobileOpen} aria-controls="mobile-navigation"
          onClick={() => { cancelClose(); setActive(''); setMobileOpen(!mobileOpen); }}
          className={`grid h-10 w-10 place-items-center rounded-xl border xl:hidden ${focusClass}`}
          style={{ color: '#0B3B78', borderColor: '#DFE7F1', backgroundColor: '#F5F8FC', outlineColor: '#2563EB' }}>
          {mobileOpen ? <X size={21} /> : <Menu size={21} />}
        </button>
      </div>
        {currentMenu && <div id={`desktop-${currentMenu.id}-menu`} key={currentMenu.id} className="absolute inset-x-0 top-full hidden xl:block"
          onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div className="max-h-[calc(100svh-77px)] overflow-y-auto rounded-none">
            <PremiumNavMenu menu={currentMenu} onNavigate={navigate} />
          </div>
        </div>}
    </nav>
    {currentMenu && <div className="fixed inset-0 top-[77px] z-40 hidden xl:block" aria-hidden="true" onClick={() => setActive('')}
      style={{ backgroundColor: 'transparent' }} />}
    {mobileOpen && <div id="mobile-navigation" role="navigation" aria-label="Mobile navigation" ref={mobileRef} className="fixed inset-0 top-[65px] z-40 overflow-y-auto overscroll-contain px-5 pb-8 pt-5 xl:hidden" style={{ backgroundColor: '#F7F9FC' }}>
      <p className="mb-4 px-1 text-[10px] font-bold tracking-[0.18em]" style={{ color: '#73859B' }}>EXPLORE KCS TECH</p>
      <Link to="/" onClick={navigate} className={`mb-2 block rounded-xl border px-4 py-4 text-base font-semibold ${focusClass}`} style={{ color: '#123454', backgroundColor: '#FFFFFF', borderColor: '#E2EAF3' }}>Home</Link>
      {navigationMenus.map(menu => <div key={menu.id} className="mb-2">
        <button type="button" aria-expanded={mobileActive === menu.id} aria-controls={mobileActive === menu.id ? `mobile-${menu.id}-menu` : undefined}
          onClick={() => setMobileActive(mobileActive === menu.id ? '' : menu.id)} className={`flex w-full items-center justify-between rounded-xl border px-4 py-4 ${focusClass}`}
          style={{ color: '#123454', backgroundColor: '#FFFFFF', borderColor: '#E2EAF3', outlineColor: '#2563EB' }}>
          <span className="text-base font-semibold">{menu.label}</span><ChevronDown size={16} className={`transition-transform ${mobileActive === menu.id ? 'rotate-180' : ''}`} />
        </button>
        {mobileActive === menu.id && <div id={`mobile-${menu.id}-menu`} className="pt-2"><PremiumNavMenu menu={menu} onNavigate={navigate} mobile /></div>}
      </div>)}
      <div className="mt-6"><ContactButton onClick={navigate} mobile /></div>
    </div>}
  </>;
}
